import React, { useState, useEffect } from "react";
import { TrendingUp, TrendingDown, Search } from "lucide-react";

function CryptoCard({ name, symbol, price, change, volume, marketCap, image }) {
  const isPositive = change >= 0;
  const changeColor = isPositive ? "text-green-500" : "text-red-500";
  const TrendingIcon = isPositive ? TrendingUp : TrendingDown;

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl hover:scale-105 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <img src={image} alt={name} className="w-10 h-10 mr-3" />
          <div>
            <h3 className="text-lg text-gray-800 font-semibold">{name}</h3>
            <p className="text-gray-500 text-sm">{symbol.toUpperCase()}</p>
          </div>
        </div>
        <div className={`flex items-center ${changeColor}`}>
          <TrendingIcon size={16} className="mr-1" />
          <span className="font-medium text-gray-800">{change.toFixed(2)}%</span>
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-2xl text-gray-800 font-bold">${price.toLocaleString()}</p>
        <div className="flex justify-between text-sm text-gray-500">
          <span>Volume: ${(volume / 1e9).toFixed(2)}B</span>
          <span>Market Cap: ${(marketCap / 1e9).toFixed(2)}B</span>
        </div>
        <button className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded-full hover:bg-blue-700 transition-colors">
          Trade Now
        </button>
      </div>
    </div>
  );
}

function CryptoList() {
  const [cryptos, setCryptos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCount, setVisibleCount] = useState(10);

  useEffect(() => {
    fetch(
      "https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=250&page=1"
    )
      .then((res) => res.json())
      .then((data) => setCryptos(data))
      .catch((error) => console.error("Error fetching data:", error));
  }, []);

  const filteredCryptos = cryptos.filter(
    (crypto) =>
      crypto.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      crypto.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <section className="py-16 bg-gray-800 text-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-light text-center mb-8">
          Top Cryptocurrencies
        </h2>
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-center">
          <div className="relative w-full sm:w-64 mb-4 sm:mb-0">
            <input
              type="text"
              placeholder="Search cryptocurrencies"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 text-black"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              className="absolute left-3 top-2.5 text-gray-400"
              size={20}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {filteredCryptos.slice(0, visibleCount).map((crypto) => (
            <CryptoCard
              key={crypto.id}
              name={crypto.name}
              symbol={crypto.symbol}
              price={crypto.current_price}
              change={crypto.price_change_percentage_24h}
              volume={crypto.total_volume}
              marketCap={crypto.market_cap}
              image={crypto.image}
            />
          ))}
        </div>
        {visibleCount < filteredCryptos.length && (
          <div className="text-center mt-6">
            <button
              onClick={() => setVisibleCount((prev) => prev + 25)}
              className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition"
            >
              Show More
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default CryptoList;